import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Loader2, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useSavedLooks } from "../contexts/SavedLooksContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedLooksPage() {
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  const { toggleSave } = useSavedLooks();
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!customer?.customer_id) { setLoading(false); return; }
    // `axios`, `API` are module-level imports — not part of the dep array
    // by design. `customer.customer_id` is the only reactive value.
    axios.get(`${API}/saved-looks`, { headers: { "x-customer-id": customer.customer_id } })
      .then(res => setLooks(res.data.saved_looks || []))
      .catch((err) => { console.warn("[SavedLooks] fetch failed:", err); })
      .finally(() => setLoading(false));
  }, [customer?.customer_id]);

  const handleRemove = async (outfitId) => {
    setRemovingId(outfitId);
    await toggleSave(outfitId);
    setLooks(prev => prev.filter(l => l.outfit.id !== outfitId));
    setRemovingId(null);
  };

  if (!customer) {
    return (
      <div className="min-h-screen bg-[#FAF8F0]">
        <Navbar />
        <div className="text-center py-24">
          <Heart className="w-10 h-10 text-[#676664]/20 mx-auto mb-3" strokeWidth={1} />
          <p className="text-[15px] text-[#676664]/60 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Sign in to view your saved looks</p>
          <Link to="/login" className="text-[11px] tracking-[0.12em] uppercase text-[#9A7B4F] border-b border-[#9A7B4F]/30 pb-0.5">Sign In</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="text-[#676664]/40 hover:text-[#676664]"><ArrowLeft className="w-4 h-4" /></button>
          <h1 className="text-xl lg:text-2xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Saved Looks
          </h1>
          <span className="text-[11px] text-[#676664]/50">{looks.length} {looks.length === 1 ? 'item' : 'items'}</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
        ) : looks.length === 0 ? (
          <div className="text-center py-20" data-testid="saved-looks-empty">
            <Heart className="w-12 h-12 text-[#676664]/15 mx-auto mb-4" strokeWidth={1} />
            <p className="text-[16px] text-[#676664]/50" style={{ fontFamily: "'Cormorant Garamond', serif" }}>No saved looks yet</p>
            <Link to="/" className="inline-block mt-4 text-[11px] tracking-[0.12em] uppercase text-[#9A7B4F] border-b border-[#9A7B4F]/30 pb-0.5">
              Browse Collection
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="saved-looks-grid">
            {looks.map(({ outfit, saved_at }) => {
              const price = outfit.rental_options?.[0]?.price_4_day || outfit.price_4_day || 0;
              return (
                <div key={outfit.id} className="group relative" data-testid={`saved-look-${outfit.id}`}>
                  <Link to={`/outfit/${outfit.id}`}>
                    <div className="aspect-[3/4] bg-[#f5f3f0] overflow-hidden mb-2">
                      <img src={outfit.images?.[0] || ""} alt={outfit.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                  </Link>
                  <button
                    onClick={() => handleRemove(outfit.id)}
                    disabled={removingId === outfit.id}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                    data-testid={`unsave-${outfit.id}`}
                  >
                    {removingId === outfit.id ? <Loader2 className="w-4 h-4 animate-spin text-[#676664]" /> : <Heart className="w-4 h-4 fill-[#9A7B4F] text-[#9A7B4F]" />}
                  </button>
                  <Link to={`/outfit/${outfit.id}`}>
                    <p className="text-[13px] text-[#282828] truncate">{outfit.name}</p>
                    {price > 0 && <p className="text-[12px] text-[#9A7B4F] mt-0.5">From ${price}</p>}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
